import style from '../styles/form.module.css'

import classNames from 'classnames/bind'
import useTranslation from 'next-translate/useTranslation'
import { useState, useEffect } from 'react'
import useAuth from '../hooks/useAuth'
import useUser from '../hooks/useUser'
import { useRouter } from 'next/router'
import { getSortedCountries } from '../helpers/countries'

export const getCreditCardFees = (chargesCC, currency, amount) => {
  if (currency === 'USD') {
    const item = chargesCC.filter(i => i.PaymentOptionCode === 'Stripe')[0]
    if (item) {
      if ((item.SurchargePct * amount + item.SurchargeFixedFee) > item.SurchargeMinimumFee) {
        return (item.SurchargePct) * amount + item.SurchargeFixedFee
      } else {
        return item.SurchargeMinimumFee
      }
    } else {
      return 0
    }
  } else if (currency === 'SGD') {
    const item = chargesCC.filter(i => i.PaymentOptionCode === 'StripeSgd')[0]
    if (item) {
      if ((item.SurchargePct * amount + item.SurchargeFixedFee) > item.SurchargeMinimumFee) {
        return (item.SurchargePct) * amount + item.SurchargeFixedFee
      } else {
        return item.SurchargeMinimumFee
      }
    } else {
      return 0
    }
  }
}

let stripeScript
let stripe
let stripeSecret
let stripeCard, stripeAddress
let interval

const countries = getSortedCountries()

export default function Stripe ({ id = '', active, amount, currency, onSubmit, onComplete, onError, onCancel }) {
  const { t } = useTranslation('main')
  const c = classNames.bind(style)
  const { locale } = useRouter()

  const { authedFetch } = useAuth()
  const { user } = useUser()
  const emails = user?.Contacts.filter(c => c.Type === 'Email') || []
  const email = emails.find(c => c.IsDefault)?.Value || emails[0]?.Value || ''

  const [stripeLoaded, setStripeLoaded] = useState()
  const [loading, setLoading] = useState(true)
  const [cardActive, setCardActive] = useState()
  const [cardComplete, setCardComplete] = useState()
  const [cardError, setCardError] = useState()
  const [addressComplete, setAddressComplete] = useState()
  const [paymentIntentId, setPaymentIntentId] = useState(id)
  const [chargesCC, setChargesCC] = useState()

  useEffect(() => () => clearInterval(interval), [])

  useEffect(() => {
    const getChargesCC = async () => {
      const response = await authedFetch('/api/chargescc')
      const json = await response.json()
      setChargesCC(json)
    }
    getChargesCC()
  }, [])

  useEffect(() => {
    if (active !== false) {
      if (!window.Stripe) {
        stripeScript = document.createElement('scri' + 'pt')
        stripeScript.src = 'https://js.stripe.com/v3/'
        stripeScript.onload = () => {
          stripe = window.Stripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY)
          setStripeLoaded(true)
        }
        document.body.appendChild(stripeScript)
      } else {
        stripe = window.Stripe(process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY)
        setStripeLoaded(true)
      }
    }
  }, [active])

  useEffect(() => {
    async function fetchRef () {
      const response = await authedFetch(`/api/stripe?amount=${amount || 0}&currency=${currency}&id=${id}`)
      const json = await response.json()
      if (response.status !== 200) {
        onError(json.detail)
        return
      }
      stripeSecret = json.clientSecret
      setPaymentIntentId(json.id)

      const elements = stripe.elements({
        locale,
        appearance: {
          theme: 'stripe',
          variables: {
            fontFamily: '"Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", system-ui, sans-serif',
            fontSizeBase: '1em',
            padding: '.5em, .8em',
            borderRadius: '.2em'
          },
          rules: {
            '.Input': {
              fontSize: '17px',
              padding: '.5em .8em',
              backgroundColor: 'whitesmoke',
              transition: 'all .2s ease-out',
              boxShadow: '0 0 0 1px #ddd',
              border: '0'
            },
            '.Input:focus': {
              backgroundColor: '#fff',
              boxShadow: '0 0 0 2px #aa8941',
              outline: 'none'
            },
            '.Input--invalid': {
              color: 'black',
              boxShadow: '0 0 0 2px orange'
            },
            '.Input--invalid:focus': {
              boxShadow: '0 0 0 2px orange'
            },
            '.Error': {
              color: 'orange',
              fontSize: '.875rem',
              margin: '.75em 0 0 0'
            }
          }
        }
      })

      stripeCard = elements.create('card', {
        hidePostalCode: true,
        iconStyle: 'solid',
        style: {
          base: {
            iconColor: '#777',
            fontFamily: '"Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", system-ui, sans-serif',
            fontSize: '17px',
            '::placeholder': {
              color: '#aaa'
            }
          },
          invalid: {
            color: '#000',
            iconColor: '#777'
          }
        }
      })

      const countryCode = countries.find(c => c.name === user.CountryOfResidence)?.code || ''
      stripeAddress = elements.create('address', {
        mode: 'billing',
        defaultValues: {
          name: `${user.FirstName} ${user.LastName}`,
          address: {
            country: countryCode
          }
        }
      })

      stripeAddress.mount('#address-element')
      stripeAddress.on('change', function (event) {
        setAddressComplete(event.complete)
      })
      setAddressComplete(false)

      stripeCard.mount('#card-element')
      stripeCard.on('focus', () => setCardActive(true))
      stripeCard.on('blur', () => setCardActive(false))
      stripeCard.on('change', function (event) {
        document.getElementById('card-error').textContent = event.error ? event.error.message : ''
        setCardError(!!event.error || event.empty)
        setCardComplete(event.complete)
      })
      setCardComplete(false)
      setLoading(false)
    }
    active !== false && currency && stripeLoaded && !stripeSecret && chargesCC && fetchRef()
  }, [active, currency, stripeLoaded, chargesCC])

  const handleSubmit = e => {
    e.preventDefault()

    async function submit () {
      if (!user) {
        return
      }
      const { value: billingDetails } = (await stripeAddress.getValue())
      const response = await stripe.confirmCardPayment(stripeSecret, {
        payment_method: { card: stripeCard, billing_details: { ...billingDetails, email } }
      })
      if (response.error) {
        setCardError(true)
        onError(response.error.message)
      } else {
        interval = setInterval(async () => {
          const res = await authedFetch('/api/payment-intent/' + paymentIntentId)
          if (res.ok) {
            const json = await res.json()
            switch (json.Status) {
              case 'Completed':
                clearInterval(interval)
                onComplete()
                break
              case 'Cancelled':
                clearInterval(interval)
                setCardError(true)
                onError(t('stripe-unknown-error'))
            }
          }
        }, 2000)
      }
    }
    submit()

    if (cardComplete && !cardError && addressComplete) {
      onSubmit()
    }
  }

  const handleCancel = () => {
    if (window.confirm(t('stripe-cancel-confirm'))) {
      onCancel()
      authedFetch('/api/payment-intent', {
        method: 'PUT',
        body: JSON.stringify({
          PaymentIntentId: id,
          Status: 'Cancelled'
        })
      })
    }
  }

  return (
    <form className={c('creditcardform')} onSubmit={handleSubmit}>
      <h3>{t('stripe-creditcard-title')}</h3>
      <p>{t('stripe-creditcard-info')}</p>
      <div className={c('inputfield')}>
        <label>{t('stripe-creditcard-input')}:</label>
        <div className={c('cardcontainer', { focus: cardActive, error: cardError }) + (loading ? ' loadingshimmer' : '')}>
          <span id='card-element' />
        </div>
        <div id='card-error' />
        <div className={c('addresscontainer', { focus: cardActive, error: cardError }) + (loading ? ' loadingshimmer' : '')}>
          <span id='address-element' />
        </div>
      </div>
      <br />
      <button className={cardComplete && addressComplete ? 'pulsate' : ''} type='submit' disabled={cardError || !addressComplete || !user} id='card-pay-button'>
        {t('stripe-creditcardcomplete-button')} {currency && chargesCC && ((!id ? (amount + getCreditCardFees(chargesCC, currency, amount)) : amount) || 0).toLocaleString(locale, { style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </button>
      <br />
      {id && (
        <>
          <br />
          <button className='light' onClick={handleCancel}>{t('stripe-cancel-button')}</button>
        </>
      )}
    </form>
  )
}
